import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GraduationCap, Info, Upload, X } from "lucide-react";
import TabsCst from "@components/TabsCst";
import UploadModalCst from "@components/UploadModalCst";
import { openToast } from "@utils/toastStore";
import {
  formatSessionOccupiedError,
  isSessionOverlapError,
  onErrorMessage,
} from "@utils/utilsApi";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import WarningDialogCst from "@components/WarningDialogCst";
import {
  StatusEnum,
  StatusModalConfirmTypes,
} from "@components/ModalConfirmsCst/types";
import EditionUsersTab from "./components/EditionUsersTab";
import GroupsTab from "../../Enrollment/steps/tabs/GroupsTab";
import { SelectedTargetItem } from "../../Enrollment/types";
import { useEditionEnrollmentSubmit } from "./hooks/useEditionEnrollmentSubmit";
import { useEditionCsvEnrollment } from "./hooks/useEditionCsvEnrollment";
import { EditionEnrollmentTriggerPayload } from "./types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

interface EditionEnrollmentModalProps {
  trigger: EditionEnrollmentTriggerPayload;
  onClose: () => void;
}

type Tab = "users" | "groups";

const CSV_ENROLLMENT_MAX_RECORDS = 1000;

const EditionEnrollmentModal: React.FC<EditionEnrollmentModalProps> = ({
  trigger,
  onClose,
}) => {
  const { t } = useTranslation("edition-enrollment");
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState<Tab>("users");
  const [users, setUsers] = useState<SelectedTargetItem[]>([]);
  const [groups, setGroups] = useState<SelectedTargetItem[]>([]);
  const [csvOpen, setCsvOpen] = useState(false);

  const { submit, isSubmitting } = useEditionEnrollmentSubmit();
  const { t: tModal } = useTranslation("modal");
  const [outcomeStatus, setOutcomeStatus] = useState<
    StatusModalConfirmTypes | undefined
  >(undefined);
  const [overlapError, setOverlapError] = useState<string | null>(null);
  const { uploadCsv, downloadTemplate, downloadEnrolled } =
    useEditionCsvEnrollment({
      editionId: trigger.editionId,
      corporateId: trigger.corporateId,
      objectId: trigger.objectId,
      kind: activeTab,
    });

  const totalSelected = users.length + groups.length;

  const tabs = [
    { key: "users", label: String(t("tabs.users")) },
    { key: "groups", label: String(t("tabs.groups")) },
  ];

  const handleConfirm = async () => {
    try {
      await submit({ trigger, users, groups });
      trigger.onSuccess?.();
      onClose();
      openToast({
        severity: "success",
        message: String(t("feedback.success")),
      });
    } catch (e: any) {
      const detail = String(
        e?.response?.data?.errors?.FATAL?.[0]?.message ??
          e?.response?.data?.message ??
          "",
      );
      if (detail.includes("MAX_EDITION_CAPACITY_REACHED")) {
        setOutcomeStatus({
          s: StatusEnum.KO,
          message: String(tModal("max-capacity-reached-error")),
        });
        return;
      }
      if (isSessionOverlapError(e)) {
        setOverlapError(formatSessionOccupiedError(e, t));
        return;
      }
      onClose();
      openToast({
        severity: "error",
        message: onErrorMessage(e) || String(t("feedback.error")),
      });
    }
  };

  return (
    <>
      <ModalConfirmsCst
        open={!!outcomeStatus}
        title={String(tModal("outcome-item"))}
        description={String(tModal("outcome-item"))}
        onCallBackClosed={() => setOutcomeStatus(undefined)}
        status={outcomeStatus}
      />
      <WarningDialogCst
        open={!!overlapError}
        title={String(t("feedback.error-title"))}
        description={overlapError ?? ""}
        closeLabel={String(t("feedback.error-close"))}
        onClose={() => setOverlapError(null)}
      />
      <Dialog
        open
        onClose={isSubmitting ? undefined : onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{ padding: "20px 24px 8px", color: theme.palette.text.primary }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.text.primary,
                }}
              >
                <GraduationCap size={16} />
                <Typography sx={{ fontSize: "13px" }}>
                  {trigger.objectTitle || "-"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mt: 0.5,
                }}
              >
                {t("title")}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="close"
              disabled={isSubmitting}
              sx={{ color: theme.palette.text.primary }}
            >
              <X size={20} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <TabsCst
              tabs={tabs}
              activeKey={activeTab}
              onChange={(k) => setActiveTab(k as Tab)}
            />
            <ButtonTertiary
              size="md"
              startIcon={<Upload size={14} />}
              onClick={() => setCsvOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              {t("upload-csv")}
            </ButtonTertiary>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            mt: 2,
            padding: "0 24px 24px",
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          }}
        >
          {activeTab === "users" && (
            <EditionUsersTab
              corporateId={trigger.corporateId}
              objectId={trigger.objectId}
              editionId={trigger.editionId}
              selected={users}
              onChange={setUsers}
            />
          )}
          {activeTab === "groups" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  padding: "12px 16px",
                  borderRadius: "4px",
                  backgroundColor: theme.palette.badge.info.background,
                  border: `1px solid ${theme.palette.badge.info.border}`,
                }}
              >
                <Info
                  size={18}
                  color={theme.palette.badge.info.border}
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: theme.palette.common.black,
                  }}
                >
                  {t("groups-limit-banner")}
                </Typography>
              </Box>
              <GroupsTab
                corporateId={trigger.corporateId}
                selected={groups}
                onChange={setGroups}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "12px 24px 20px" }}>
          <Box sx={{ flex: 1 }} />
          <ButtonPrimary
            size="md"
            onClick={handleConfirm}
            disabled={isSubmitting || totalSelected === 0}
          >
            {isSubmitting ? String(t("submitting")) : String(t("confirm"))}
          </ButtonPrimary>
        </DialogActions>
      </Dialog>

      <UploadModalCst
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        title={String(t("csv.upload-title"))}
        accept=".csv,text/csv"
        maxRecords={
          activeTab === "users" ? CSV_ENROLLMENT_MAX_RECORDS : undefined
        }
        banner={{
          ...(activeTab === "groups"
            ? {
                title: String(t("csv.banner.title")),
                text: String(t("csv.banner.text-groups")),
              }
            : {
                secondaryDownloadLabel: String(
                  t("csv.banner.download-enrolled"),
                ),
                onSecondaryDownload: downloadEnrolled,
                secondaryDownloadVariant: "contained" as const,
              }),
          downloadLabel: String(t("csv.banner.download")),
          onDownload: downloadTemplate,
        }}
        onUpload={uploadCsv}
        onUploaded={() => trigger.onSuccess?.()}
      />
    </>
  );
};

export default EditionEnrollmentModal;
